import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { server, backend_url } from "../../server";
import Ratings from "../../components/Products/Ratings";
import Loader from "../../components/Layout/Loader";

const formatDate = (value) => {
  if (!value) return "—";
  return String(value).substring(0, 10);
};

const ReviewCard = ({ title, subtitle, rating, comment, createdAt, link, linkLabel, badge }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {badge}
    </div>
    <div className="mt-2 flex items-center gap-2">
      <Ratings rating={rating || 0} />
      <span className="text-xs text-slate-500">{rating || 0} / 5</span>
    </div>
    {comment ? <p className="mt-2 text-sm text-slate-700">{comment}</p> : null}
    <p className="mt-2 text-xs text-slate-400">{formatDate(createdAt)}</p>
    {link && (
      <Link to={link} className="mt-3 inline-block text-sm font-medium text-[#38513b] hover:underline">
        {linkLabel || "View order"}
      </Link>
    )}
  </div>
);

const EmptyBlock = ({ message }) => (
  <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
    {message}
  </p>
);

const ProfileMyReviews = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await axios.get(`${server}/user/my-reviews`, {
          withCredentials: true,
        });
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  const asBuyerSeller = data?.asBuyer?.sellerService || [];
  const asBuyerProduct = data?.asBuyer?.products || [];
  const asSellerBuyer = data?.asSeller || [];
  const receivedAsSeller = data?.receivedAsSeller || [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">My reviews</h1>
        <p className="mt-1 text-sm text-slate-500">
          Reviews you have written, and feedback your buyers have left.
        </p>
      </div>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Reviews you wrote as a buyer</h2>
        <p className="text-sm text-slate-500">
          Seller service ratings and product feedback from your purchases.
        </p>
        {asBuyerSeller.length === 0 && asBuyerProduct.length === 0 ? (
          <EmptyBlock message="You have not left any buyer reviews yet. Complete an order, then use the order page or inbox prompt to rate the seller or product." />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {asBuyerSeller.map((item) => (
              <ReviewCard
                key={`buyer-seller-${item.orderId}`}
                title={`Seller: ${item.sellerName}`}
                subtitle={
                  item.orderNumber != null ? `Order #${item.orderNumber}` : "Seller service review"
                }
                rating={item.rating}
                comment={item.comment}
                createdAt={item.createdAt}
                link={item.orderLink}
                linkLabel="View order"
                badge={
                  !item.mutualComplete ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                      Awaiting seller review
                    </span>
                  ) : null
                }
              />
            ))}
            {asBuyerProduct.map((item, index) => (
              <ReviewCard
                key={`buyer-product-${item.productId}-${index}`}
                title={item.productName || "Product review"}
                subtitle="Product review"
                rating={item.rating}
                comment={item.comment}
                createdAt={item.createdAt}
                link={item.productLink}
                linkLabel="View product"
              />
            ))}
          </div>
        )}
      </section>

      {asSellerBuyer.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Reviews you wrote as a seller</h2>
          <p className="text-sm text-slate-500">Buyer ratings you submitted after completing sales.</p>
          <div className="grid gap-3 md:grid-cols-2">
            {asSellerBuyer.map((item) => (
              <ReviewCard
                key={`seller-buyer-${item.orderId}`}
                title={`Buyer: ${item.buyerName}`}
                subtitle={
                  item.orderNumber != null ? `Order #${item.orderNumber}` : "Buyer review"
                }
                rating={item.rating}
                comment={item.comment}
                createdAt={item.createdAt}
                link={item.orderLink}
                linkLabel="View seller order"
                badge={
                  !item.mutualComplete ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                      Awaiting buyer review
                    </span>
                  ) : null
                }
              />
            ))}
          </div>
        </section>
      )}

      {receivedAsSeller.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Reviews your buyers left</h2>
          <p className="text-sm text-slate-500">
            These appear on your public seller profile under Seller Reviews.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {receivedAsSeller.map((item, index) => (
              <div
                key={item.orderId || `received-${index}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={`${backend_url}${item.buyer?.avatar || "default-avatar.png"}`}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = `${backend_url}default-avatar.png`;
                    }}
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {item.buyer?.name || "Customer"}
                    </p>
                    <Ratings rating={item.rating || 0} />
                  </div>
                </div>
                {item.comment ? (
                  <p className="mt-2 text-sm text-slate-700">{item.comment}</p>
                ) : null}
                <p className="mt-2 text-xs text-slate-400">{formatDate(item.createdAt)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProfileMyReviews;
